import { GitMerge, AlertTriangle, Loader2, X as XIcon } from "lucide-react";
import { StatusBadge, formatDate } from "./CustomerBadges";

const MergeRow = ({ label, value, highlight = false }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono-tab ${highlight ? "font-semibold text-slate-900" : "text-slate-400"}`}>{value}</span>
  </div>
);

/* Merge modal — compare two customer accounts and pick the keeper */
export default function MergeCustomersModal({ modal, busy, primaryId, onPickPrimary, onCancel, onConfirm }) {
  if (!modal) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="oc-merge-modal"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-violet-600" /> Kunden zusammenführen
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Wähle, welcher Account erhalten bleibt. Der andere wird gelöscht; Mail-Historie, MT5-Daten und Vertrag werden bestmöglich verschmolzen.
            </p>
          </div>
          <button
            onClick={() => !busy && onCancel()}
            data-testid="oc-merge-close"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          {[modal.a, modal.b].map((c) => {
            const isPrimary = primaryId === c.id;
            const prog = c.onboarding_progress || {};
            const v1 = (prog.video1_checks || []).filter(Boolean).length;
            const v2 = (prog.video2_checks || []).filter(Boolean).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onPickPrimary(c.id)}
                data-testid={`oc-merge-pick-${c.id}`}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  isPrimary
                    ? "border-violet-500 bg-violet-50 ring-2 ring-violet-200"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${
                    isPrimary ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                    {isPrimary ? "✓ Behalten" : "Löschen"}
                  </span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="font-display font-semibold text-slate-900">{c.first_name} {c.last_name}</div>
                <div className="font-mono-tab text-[12px] text-slate-700 break-all mt-0.5">{c.email}</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <MergeRow label="Onboarding" value={`${v1 + v2}/13`} highlight={v1 + v2 > 0} />
                  <MergeRow label="MT5-Nr." value={c.mt5_account || "—"} highlight={!!c.mt5_account} />
                  <MergeRow
                    label="Vertrag"
                    value={c.contract?.signed_at ? "unterschrieben" : c.contract?.waived ? "befreit" : "offen"}
                    highlight={!!(c.contract?.signed_at || c.contract?.waived)}
                  />
                  <MergeRow label="Partner" value={c.partner_name || c.partner_slug || "—"} highlight={!!c.partner_slug} />
                  <MergeRow label="Geburtstag" value={c.birthdate || "—"} highlight={!!c.birthdate} />
                  <MergeRow label="Registriert" value={formatDate(c.created_at)} />
                </div>
              </button>
            );
          })}
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 mb-4 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Vor der Bestätigung: prüfe beide Accounts in der Kundenliste auf wichtige Daten.
            Das Passwort des „Löschen"-Accounts kann <strong>nicht</strong> wiederhergestellt werden — der Kunde meldet sich danach nur noch mit dem Passwort des Behalten-Accounts an.
          </span>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="oc-merge-cancel"
            className="al-cta-ghost text-sm"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !primaryId}
            data-testid="oc-merge-confirm"
            className="al-cta text-sm disabled:opacity-50"
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Führe zusammen…</> : <><GitMerge className="h-4 w-4" /> Jetzt zusammenführen</>}
          </button>
        </div>
      </div>
    </div>
  );
}
